import { GUARANTEED_ALWAYS } from "@/state/randomizationConstants/constants";
import {
  APP_TO_BUILD,
  PlumbingPracticeTestType,
  PracticeTestType,
  ElectricalPracticeTestType,
  ElectricalUSPracticeTestType,
  IbewAptitudePracticeTestType,
} from "../../appsConfig";
import RootStoreInstance from "@/state/RootStore";
import posthog from "@/utils/posthog";

export const getPracticeTestTypesElectricalCA = () => {
  const store = RootStoreInstance;
  const stateSelected = store.mainAppStore.selectedStateCode || "";

  // Quick practice section
  let quickPracticeTypes: any[] = [
    ElectricalPracticeTestType.QUICK,
    ElectricalPracticeTestType.BANNER_AI_CHAT,
  ];

  // General section
  let generalTestTypes = [
    ElectricalPracticeTestType.STANDARD,
    ElectricalPracticeTestType.RED_SEAL_EXAM,
  ];

  // Category Modes section
  let categoryTestTypes = [
    ElectricalPracticeTestType.CIRCUIT_CONCEPTS,
    ElectricalPracticeTestType.OCCUPATIONAL_SKILLS,
    ElectricalPracticeTestType.SERVICE_DISTRIBUTION,
    ElectricalPracticeTestType.WIRING_SYSTEMS,
  ];

  // Special Modes section
  let specialTestTypes = [];

  // Add exam-level specific categories
  if (stateSelected.includes("L3")) {
    categoryTestTypes.push(ElectricalPracticeTestType.MOTOR_CONTROL_SYSTEMS);
  } else if (stateSelected.includes("L4")) {
    categoryTestTypes.push(ElectricalPracticeTestType.COMMUNICATION_SYSTEMS);
    categoryTestTypes.push(ElectricalPracticeTestType.SPECIAL_SYSTEMS);
  } else if (!stateSelected.includes("L1") && !stateSelected.includes("L2")) {
    // For non-L1/L2/L3/L4, add all remaining categories
    categoryTestTypes.push(ElectricalPracticeTestType.MOTOR_CONTROL_SYSTEMS);
    categoryTestTypes.push(ElectricalPracticeTestType.COMMUNICATION_SYSTEMS);
    categoryTestTypes.push(ElectricalPracticeTestType.SPECIAL_SYSTEMS);
  }

  // Add special modes
  const is_2024 = stateSelected.includes("2024");
  if (is_2024) {
    specialTestTypes.push(ElectricalPracticeTestType.UPDATES_2024);
  }

  specialTestTypes.push(ElectricalPracticeTestType.NO_CODEBOOK);

  // Calculations advertises 25 questions, and Level 4 does not have 25 to give: the eligible
  // CALC_ONLY bank holds 20 for L4 2021, 19 for L4 2024, 17 for L4 Industrial 2021 and 16 for
  // L4 Industrial 2024. Starting the mode threw "Question pool exhausted". Every other level
  // has 81 or more, so this is the one place the advertised size outruns the bank.
  //
  // `includes("L4")` covers L4_2021/L4_2024 and the Industrial L4_IN_2021/L4_IN_2024. Red Seal
  // Industrial is RS_IN_*, so it is correctly untouched.
  if (!stateSelected.includes("L4")) {
    specialTestTypes.push(ElectricalPracticeTestType.CALCULATION_ONLY);
  }

  return {
    quickPracticeTypes: quickPracticeTypes.filter(
      (testType: any) => testType !== null
    ),
    generalTestTypes: generalTestTypes.filter(
      (testType: any) => testType !== null
    ),
    categoryTestTypes: categoryTestTypes.filter(
      (testType: any) => testType !== null
    ),
    specialTestTypes: specialTestTypes.filter(
      (testType: any) => testType !== null
    ),
  };
};

const necExamType = "EXAM_SIMULATION";

export const getPracticeTestTypesElectricalUS = () => {
  const store = RootStoreInstance;
  const stateSelected = store.mainAppStore.selectedStateCode || "";
  const questions = store.questionsStore.questions;
  const curriculumSplits = store.questionsStore.curriculum || {};

  // Quick practice section
  let quickPracticeTypes = [
    PracticeTestType.quickPracticeType,
    PracticeTestType.aiChat,
  ];

  // General section
  let generalTestTypes = [PracticeTestType.standardType, necExamType];

  // Category Modes section
  let categoryTestTypes = [
    PracticeTestType.enum.OCCUPATIONAL_SKILLS,
    PracticeTestType.enum.SERVICE_DISTRIBUTION,
    PracticeTestType.enum.WIRING_SYSTEMS,
    PracticeTestType.enum.MOTOR_CONTROL_SYSTEMS,
    PracticeTestType.enum.SPECIAL_CONDITIONS,
    PracticeTestType.enum.COMMUNICATION_SYSTEMS,
  ];

  // Special Modes section
  let specialTestTypes = [];

  const stateYear = store.questionsStore.getEffectiveCodebookYear(stateSelected);
  const show2023 = stateYear === 2023;

  if (show2023) {
    specialTestTypes.push(PracticeTestType.updates2023Type);
  }

  specialTestTypes.push(PracticeTestType.calculationOnlyType);

  // Need to add the no codebook type only if the state doesn't say it's guaranteed to always be codebook
  const sampleCodebookSplits = curriculumSplits["1"]["codebook"] || {};
  if (typeof sampleCodebookSplits === "object") {
    const codebookSplit = sampleCodebookSplits?.[stateSelected];
    if (codebookSplit !== GUARANTEED_ALWAYS) {
      specialTestTypes.push(PracticeTestType.noCodebookType);
    }
  }

  const stateAmendmentTypeToFind = `${stateSelected}_AMEND_${stateYear}`;
  const allQuestionsMatching = Array.from(questions.values()).filter(
    (question: any) => {
      return question.special_types?.includes(stateAmendmentTypeToFind);
    }
  );

  // Need at least 25 matching questions, otherwise do not show the state amendment type
  if (allQuestionsMatching?.length >= 25) {
    specialTestTypes.push(PracticeTestType.stateAmendmentsType);
  }

  return {
    quickPracticeTypes: quickPracticeTypes.filter((type) => type !== null),
    generalTestTypes: generalTestTypes.filter((type) => type !== null),
    categoryTestTypes: categoryTestTypes.filter((type) => type !== null),
    specialTestTypes: specialTestTypes.filter((type) => type !== null),
  };
};

export const getCodebookHomeCard = (): ElectricalUSPracticeTestType | null => {
  switch (APP_TO_BUILD) {
    case "electrical_us":
      return ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION;
    default:
      return null;
  }
};

export const getPracticeTestTypesPlumbing = () => {
  // Quick practice section
  let quickPracticeTypes: any[] = [
    PlumbingPracticeTestType.QUICK,
    PlumbingPracticeTestType.BANNER_AI_CHAT,
  ];

  // General section
  let generalTestTypes = [
    PlumbingPracticeTestType.STANDARD,
    PlumbingPracticeTestType.RED_SEAL_EXAM,
  ];

  // Category Modes section
  let categoryTestTypes = [
    PlumbingPracticeTestType.OCCUPATIONAL,
    PlumbingPracticeTestType.PIPE,
    PlumbingPracticeTestType.SEWAGE,
    PlumbingPracticeTestType.SERVICE,
    PlumbingPracticeTestType.FIXTURES,
    PlumbingPracticeTestType.STEAM,
    PlumbingPracticeTestType.SPECIALIZED,
  ];

  // Special Modes section
  let specialTestTypes = [PlumbingPracticeTestType.NO_CODEBOOK];

  return {
    quickPracticeTypes,
    generalTestTypes,
    categoryTestTypes,
    specialTestTypes,
  };
};

export const getPracticeTestTypesIbewAptitude = () => {
  // Quick practice section
  let quickPracticeTypes = [
    PracticeTestType.quickPracticeType,
    PracticeTestType.aiChat,
  ];

  // General section
  let generalTestTypes = [
    PracticeTestType.standardType,
    IbewAptitudePracticeTestType.APTITUDE_EXAM,
  ];

  // Category Modes section - Math Topics
  let categoryTestTypes = [
    IbewAptitudePracticeTestType.READING_COMPREHENSION,
    IbewAptitudePracticeTestType.FRACTIONS_DECIMALS,
    IbewAptitudePracticeTestType.ORDER_OF_OPERATIONS,
    IbewAptitudePracticeTestType.SEQUENCES,
    IbewAptitudePracticeTestType.GRAPHS_FUNCTIONS,
    IbewAptitudePracticeTestType.SLOPES_LINEAR,
    IbewAptitudePracticeTestType.POLYNOMIAL_OPERATIONS,
    IbewAptitudePracticeTestType.QUADRATICS,
    IbewAptitudePracticeTestType.SYSTEMS_OF_EQUATIONS,
    IbewAptitudePracticeTestType.WORD_LOGIC,
  ];

  // Special Modes section - none for IBEW aptitude test
  let specialTestTypes: any[] = [];

  return {
    quickPracticeTypes: quickPracticeTypes.filter((type) => type !== null),
    generalTestTypes: generalTestTypes.filter((type) => type !== null),
    categoryTestTypes: categoryTestTypes.filter((type) => type !== null),
    specialTestTypes: specialTestTypes.filter((type) => type !== null),
  };
};
